import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useMemo, useState } from "react";
import Header from "../Component/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { supabase } from "../supabase/client";

const WEEKDAYS = [
  { key: "mon", label: "월" },
  { key: "tue", label: "화" },
  { key: "wed", label: "수" },
  { key: "thu", label: "목" },
  { key: "fri", label: "금" },
  { key: "sat", label: "토" },
  { key: "sun", label: "일" },
];

const RULE_TYPES = [
  { value: "weekday_fixed", label: "요일별 고정" },
  { value: "rotation", label: "순번제" },
  { value: "balanced", label: "균등 배정" },
];

const ROUTE_KEY_SEPARATOR = "|||";

function normalizeRouteTime(value = "") {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function makeRouteKey(route = "", time = "") {
  const routeValue = String(route || "").trim();
  const timeValue = normalizeRouteTime(time);
  return timeValue ? `${routeValue}${ROUTE_KEY_SEPARATOR}${timeValue}` : routeValue;
}

function parseRouteKey(key = "") {
  const [route = "", time = ""] = String(key || "").split(ROUTE_KEY_SEPARATOR);
  return {
    route: route.trim(),
    time: normalizeRouteTime(time),
  };
}

function formatRouteLabel(route = "", time = "") {
  const timeValue = normalizeRouteTime(time);
  return timeValue ? `${route} · ${timeValue}` : route;
}

function getAllowedRouteKeys(form, driverId) {
  const saved = form.driverRoutePermissions?.[driverId];
  return Array.isArray(saved) ? saved : form.selectedRoutes;
}

function buildDriverRoutePermissionsPayload(form) {
  if (form.rule_type !== "rotation" && form.rule_type !== "balanced") return {};

  return form.selectedDrivers.reduce((permissions, driverId) => {
    const allowedRoutes = getAllowedRouteKeys(form, driverId).filter((routeKey) => form.selectedRoutes.includes(routeKey));
    permissions[driverId] = allowedRoutes;
    return permissions;
  }, {});
}

function getRoutePermissionProblems(form) {
  if (form.rule_type !== "rotation" && form.rule_type !== "balanced") return [];

  return form.selectedRoutes.filter((routeKey) =>
    form.selectedDrivers.every((driverId) => !getAllowedRouteKeys(form, driverId).includes(routeKey))
  );
}

const emptyForm = {
  id: null,
  name: "",
  route_pattern: "",
  route_time: "",
  route_lookup_date: "",
  ruleGroup: "",
  match_type: "exact",
  rule_type: "weekday_fixed",
  priority: 10,
  enabled: true,
  memo: "",
  weekdays: ["mon", "tue", "wed", "thu", "fri"],
  selectedRoutes: [],
  selectedDrivers: [],
  weekdayDrivers: {},
  rotationStartDriverId: "",
  driverRoutePermissions: {},
};

export default function BusDispatchRules() {
  const [rules, setRules] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [routeOptions, setRouteOptions] = useState([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedDriverRows = useMemo(
    () => drivers.filter((driver) => form.selectedDrivers.includes(driver.id)),
    [drivers, form.selectedDrivers]
  );

  const availableRouteOptions = useMemo(() => {
    const map = new Map(routeOptions.map((option) => [option.key, option]));
    form.selectedRoutes.forEach((routeKey) => {
      if (routeKey && !map.has(routeKey)) {
        const parsed = parseRouteKey(routeKey);
        map.set(routeKey, { key: routeKey, route: parsed.route, time: parsed.time, count: 0 });
      }
    });
    if (form.route_pattern) {
      const key = makeRouteKey(form.route_pattern, form.route_time);
      if (!map.has(key)) map.set(key, { key, route: form.route_pattern, time: form.route_time, count: 0 });
    }
    return Array.from(map.values()).sort((a, b) => {
      const routeCompare = a.route.localeCompare(b.route, "ko", { numeric: true });
      if (routeCompare !== 0) return routeCompare;
      return normalizeRouteTime(a.time).localeCompare(normalizeRouteTime(b.time));
    });
  }, [form.route_pattern, form.route_time, form.selectedRoutes, routeOptions]);

  const ruleGroupOptions = useMemo(() => {
    const groups = new Set();
    rules.forEach((rule) => {
      const group = String(rule.options_json?.rule_group || "").trim();
      if (group) groups.add(group);
    });
    return Array.from(groups).sort((a, b) => a.localeCompare(b, "ko", { numeric: true }));
  }, [rules]);

  const fetchData = async () => {
    const company = localStorage.getItem("email");
    if (!company) return;

    setLoading(true);
    const [rulesResult, driversResult] = await Promise.all([
      supabase
        .from("dispatch_rules")
        .select("*, dispatch_rule_drivers(*), dispatch_rule_routes(*)")
        .eq("company", company)
        .order("priority", { ascending: true })
        .order("created_at", { ascending: false }),
      supabase
        .from("profiles")
        .select("id, driver_name, car_number, phone")
        .eq("company", company)
        .eq("role", "driver")
        .order("driver_name", { ascending: true }),
    ]);
    setLoading(false);

    if (rulesResult.error) {
      showUserAlert("배차 규칙을 불러오지 못했습니다: " + rulesResult.error.message);
      setRules([]);
    } else {
      setRules(rulesResult.data || []);
    }

    if (driversResult.error) {
      showUserAlert("기사 정보를 불러오지 못했습니다: " + driversResult.error.message);
      setDrivers([]);
    } else {
      setDrivers(driversResult.data || []);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleWeekday = (weekday) => {
    setForm((prev) => {
      const exists = prev.weekdays.includes(weekday);
      const weekdays = exists
        ? prev.weekdays.filter((item) => item !== weekday)
        : [...prev.weekdays, weekday];
      return { ...prev, weekdays };
    });
  };

  const toggleDriver = (driverId) => {
    setForm((prev) => {
      const exists = prev.selectedDrivers.includes(driverId);
      const selectedDrivers = exists
        ? prev.selectedDrivers.filter((id) => id !== driverId)
        : [...prev.selectedDrivers, driverId];
      const weekdayDrivers = { ...prev.weekdayDrivers };
      const driverRoutePermissions = { ...prev.driverRoutePermissions };
      if (exists) {
        Object.keys(weekdayDrivers).forEach((day) => {
          if (weekdayDrivers[day] === driverId) weekdayDrivers[day] = "";
        });
        delete driverRoutePermissions[driverId];
      }
      return {
        ...prev,
        selectedDrivers,
        weekdayDrivers,
        driverRoutePermissions,
        rotationStartDriverId: exists && prev.rotationStartDriverId === driverId ? "" : prev.rotationStartDriverId,
      };
    });
  };

  const toggleRoute = (route) => {
    setForm((prev) => {
      const exists = prev.selectedRoutes.includes(route);
      const selectedRoutes = exists
        ? prev.selectedRoutes.filter((item) => item !== route)
        : [...prev.selectedRoutes, route];
      const firstRoute = selectedRoutes[0] ? parseRouteKey(selectedRoutes[0]) : { route: "", time: "" };
      const driverRoutePermissions = Object.fromEntries(
        Object.entries(prev.driverRoutePermissions || {}).map(([driverId, routeKeys]) => [
          driverId,
          Array.isArray(routeKeys)
            ? routeKeys.filter((routeKey) => selectedRoutes.includes(routeKey))
            : routeKeys,
        ])
      );
      return {
        ...prev,
        selectedRoutes,
        route_pattern: firstRoute.route,
        route_time: firstRoute.time,
        driverRoutePermissions,
      };
    });
  };

  const toggleDriverRoutePermission = (driverId, routeKey) => {
    setForm((prev) => {
      const current = Array.isArray(prev.driverRoutePermissions?.[driverId])
        ? prev.driverRoutePermissions[driverId]
        : prev.selectedRoutes;
      const allowedRoutes = current.includes(routeKey)
        ? current.filter((item) => item !== routeKey)
        : [...current, routeKey];
      return {
        ...prev,
        driverRoutePermissions: {
          ...prev.driverRoutePermissions,
          [driverId]: allowedRoutes,
        },
      };
    });
  };

  const setWeekdayDriver = (weekday, driverId) => {
    setForm((prev) => ({
      ...prev,
      weekdayDrivers: { ...prev.weekdayDrivers, [weekday]: driverId },
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const editRule = (rule) => {
    const linkedDrivers = [...(rule.dispatch_rule_drivers || [])].sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
    const linkedRoutes = [...(rule.dispatch_rule_routes || [])].sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
    const selectedDrivers = Array.from(new Set(linkedDrivers.map((row) => row.driver_profile_id).filter(Boolean)));
    const selectedRoutes = linkedRoutes.map((row) => makeRouteKey(row.route_user, row.route_time)).filter(Boolean);
    const firstRoute = selectedRoutes[0] ? parseRouteKey(selectedRoutes[0]) : { route: rule.route_pattern || "", time: "" };
    const options = rule.options_json || {};
    const rotationStartIndex = Math.max(0, Number(options.rotation_start_index) || 0);
    const weekdayDrivers = {};
    linkedDrivers.forEach((row) => {
      if (row.weekday && row.driver_profile_id) weekdayDrivers[row.weekday] = row.driver_profile_id;
    });

    setForm({
      id: rule.id,
      name: rule.name || "",
      route_pattern: firstRoute.route || rule.route_pattern || "",
      route_time: firstRoute.time || "",
      route_lookup_date: "",
      ruleGroup: options.rule_group || "",
      match_type: "exact",
      rule_type: rule.rule_type || "weekday_fixed",
      priority: rule.priority ?? 10,
      enabled: rule.enabled ?? true,
      memo: rule.memo || "",
      weekdays: Array.isArray(rule.weekdays) ? rule.weekdays : ["mon", "tue", "wed", "thu", "fri"],
      selectedRoutes: selectedRoutes.length > 0 ? selectedRoutes : (rule.route_pattern ? [rule.route_pattern] : []),
      selectedDrivers,
      weekdayDrivers,
      rotationStartDriverId: selectedDrivers[rotationStartIndex] || selectedDrivers[0] || "",
      driverRoutePermissions: options.driver_route_permissions || {},
    });
  };

  const buildDriverRows = (ruleId, company) => {
    if (form.rule_type === "weekday_fixed") {
      return form.weekdays
        .map((weekday, index) => {
          const driver = drivers.find((item) => item.id === form.weekdayDrivers[weekday]);
          if (!driver) return null;
          return {
            company,
            rule_id: ruleId,
            driver_profile_id: driver.id,
            driver_name: driver.driver_name || null,
            car_number: driver.car_number || null,
            weekday,
            order_no: index + 1,
            weight: 1,
          };
        })
        .filter(Boolean);
    }

    return selectedDriverRows.map((driver, index) => ({
      company,
      rule_id: ruleId,
      driver_profile_id: driver.id,
      driver_name: driver.driver_name || null,
      car_number: driver.car_number || null,
      weekday: null,
      order_no: index + 1,
      weight: 1,
    }));
  };

  const buildRouteRows = (ruleId, company) => {
    const routes = form.rule_type === "rotation" || form.rule_type === "balanced"
      ? form.selectedRoutes
      : [makeRouteKey(form.route_pattern, form.route_time)].filter(Boolean);

    return routes.map((routeKey, index) => {
      const parsed = parseRouteKey(routeKey);
      return {
        company,
        rule_id: ruleId,
        route_user: parsed.route,
        route_time: parsed.time || null,
        order_no: index + 1,
      };
    });
  };

  const handleSave = async () => {
    const company = localStorage.getItem("email");
    const companyName = localStorage.getItem("companyName") || company;
    if (!company) return showUserAlert("회사 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
    if (!form.name.trim()) return showUserAlert("규칙명을 입력해 주세요.");
    if (form.rule_type === "rotation" || form.rule_type === "balanced") {
      if (form.selectedRoutes.length === 0) return showUserAlert(`${form.rule_type === "rotation" ? "순번제" : "균등 배정"} 규칙은 적용 노선을 하나 이상 선택해 주세요.`);
      if (form.selectedDrivers.length === 0) return showUserAlert("후보 기사를 하나 이상 선택해 주세요.");
      const permissionProblems = getRoutePermissionProblems(form);
      if (permissionProblems.length > 0) {
        const route = parseRouteKey(permissionProblems[0]);
        return showUserAlert(`'${formatRouteLabel(route.route, route.time)}' 노선을 운행할 수 있는 후보 기사를 하나 이상 체크해 주세요.`);
      }
      if (form.rule_type === "rotation" && form.selectedRoutes.length > form.selectedDrivers.length) {
        return showUserAlert("순번제에서는 적용 노선 수가 후보 기사 수보다 많을 수 없습니다.");
      }
    } else if (!form.route_pattern.trim()) {
      return showUserAlert("적용 노선명을 입력해 주세요.");
    }
    if (form.weekdays.length === 0) return showUserAlert("적용 요일을 하나 이상 선택해 주세요.");
    if (form.rule_type === "weekday_fixed" && form.weekdays.some((day) => !form.weekdayDrivers[day])) {
      return showUserAlert("요일별 고정 규칙은 선택한 모든 요일에 담당 기사를 지정해야 합니다.");
    }
    if (form.rule_type !== "weekday_fixed" && form.selectedDrivers.length === 0) {
      return showUserAlert("후보 기사를 하나 이상 선택해 주세요.");
    }

    setSaving(true);
    try {
      const rotationDriverRows = selectedDriverRows;
      const rotationStartIndex = form.rule_type === "rotation"
        ? Math.max(0, rotationDriverRows.findIndex((driver) => driver.id === form.rotationStartDriverId))
        : 0;
      const payload = {
        company,
        company_name: companyName,
        name: form.name.trim(),
        route_pattern: (form.rule_type === "rotation" || form.rule_type === "balanced" ? parseRouteKey(form.selectedRoutes[0]).route : form.route_pattern).trim(),
        match_type: "exact",
        weekdays: form.weekdays,
        rule_type: form.rule_type,
        priority: Number(form.priority) || 10,
        enabled: !!form.enabled,
        memo: form.memo || null,
        options_json: {
          rule_type: form.rule_type,
          rule_group: form.ruleGroup.trim() || null,
          weekday_drivers: form.weekdayDrivers,
          selected_routes: form.rule_type === "rotation" || form.rule_type === "balanced"
            ? form.selectedRoutes.map((routeKey) => parseRouteKey(routeKey))
            : [makeRouteKey(form.route_pattern, form.route_time)].filter(Boolean).map((routeKey) => parseRouteKey(routeKey)),
          selected_drivers: rotationDriverRows.map((driver) => driver.id),
          driver_route_permissions: buildDriverRoutePermissionsPayload(form),
          rotation_start_index: rotationStartIndex,
          rotation_start_driver_id: form.rule_type === "rotation" ? rotationDriverRows[rotationStartIndex]?.id || null : null,
          rotation_start_driver_name: form.rule_type === "rotation" ? rotationDriverRows[rotationStartIndex]?.driver_name || null : null,
        },
      };

      const ruleResult = form.id
        ? await supabase.from("dispatch_rules").update(payload).eq("id", form.id).eq("company", company).select("id").single()
        : await supabase.from("dispatch_rules").insert(payload).select("id").single();

      if (ruleResult.error) throw ruleResult.error;

      const ruleId = ruleResult.data.id;
      const { error: deleteError } = await supabase
        .from("dispatch_rule_drivers")
        .delete()
        .eq("rule_id", ruleId)
        .eq("company", company);
      if (deleteError) throw deleteError;

      const { error: deleteRouteError } = await supabase
        .from("dispatch_rule_routes")
        .delete()
        .eq("rule_id", ruleId)
        .eq("company", company);
      if (deleteRouteError) throw deleteRouteError;

      const driverRows = buildDriverRows(ruleId, company);
      if (driverRows.length > 0) {
        const { error: driverError } = await supabase.from("dispatch_rule_drivers").insert(driverRows);
        if (driverError) throw driverError;
      }

      const routeRows = buildRouteRows(ruleId, company);
      if (routeRows.length > 0) {
        const { error: routeError } = await supabase.from("dispatch_rule_routes").insert(routeRows);
        if (routeError) throw routeError;
      }

      showUserAlert("배차 규칙이 저장되었습니다.");
      resetForm();
      await fetchData();
    } catch (error) {
      showUserAlert("저장 실패: " + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const loadRouteOptions = async () => {
    const company = localStorage.getItem("email");
    if (!company) return showUserAlert("회사 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
    if (!form.route_lookup_date) return showUserAlert("노선 조회일을 선택해 주세요.");

    setRouteLoading(true);
    const rows = [];
    const pageSize = 1000;
    let error = null;

    for (let from = 0; ; from += pageSize) {
      const to = from + pageSize - 1;
      const { data, error: pageError } = await supabase
        .from("reservations")
        .select("user, time, date")
        .eq("company", company)
        .eq("date", form.route_lookup_date)
        .order("user", { ascending: true })
        .order("time", { ascending: true })
        .range(from, to);

      if (pageError) {
        error = pageError;
        break;
      }

      rows.push(...(data || []));
      if (!data || data.length < pageSize) break;
    }

    setRouteLoading(false);

    if (error) {
      showUserAlert("노선 목록을 불러오지 못했습니다: " + error.message);
      setRouteOptions([]);
      return;
    }

    const options = Array.from(
      rows.reduce((map, row) => {
        const route = String(row.user || "").trim();
        if (!route) return map;
        const time = normalizeRouteTime(row.time);
        const key = makeRouteKey(route, time);
        const item = map.get(key) || { key, route, time, count: 0, firstDate: row.date, lastDate: row.date };
        item.count += 1;
        if (row.date < item.firstDate) item.firstDate = row.date;
        if (row.date > item.lastDate) item.lastDate = row.date;
        map.set(key, item);
        return map;
      }, new Map()).values()
    ).sort((a, b) => {
      const routeCompare = a.route.localeCompare(b.route, "ko", { numeric: true });
      if (routeCompare !== 0) return routeCompare;
      return normalizeRouteTime(a.time).localeCompare(normalizeRouteTime(b.time));
    });

    setRouteOptions(options);
    if (options.length === 0) showUserAlert("선택한 노선 조회일에 예약 노선이 없습니다.");
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`'${rule.name}' 규칙을 삭제할까요?`)) return;

    const company = localStorage.getItem("email") || "";
    const { error } = await supabase
      .from("dispatch_rules")
      .delete()
      .eq("id", rule.id)
      .eq("company", company);
    if (error) {
      showUserAlert("삭제 실패: " + error.message);
      return;
    }

    if (form.id === rule.id) resetForm();
    await fetchData();
  };

  const formatRuleType = (value) => RULE_TYPES.find((item) => item.value === value)?.label || value;
  const formatWeekdays = (weekdays = []) =>
    WEEKDAYS.filter((day) => weekdays.includes(day.key)).map((day) => day.label).join(", ");
  const formatRuleRoutes = (rule) => {
    const routes = [...(rule.dispatch_rule_routes || [])]
      .sort((a, b) => (a.order_no || 0) - (b.order_no || 0))
      .map((row) => formatRouteLabel(row.route_user, row.route_time))
      .filter(Boolean);
    if (routes.length === 0) return rule.route_pattern || "";
    if (routes.length === 1) return routes[0];
    return `${routes[0]} 외 ${routes.length - 1}개`;
  };

  return (
    <div>
      <Header />
      <div className="px-4 pt-1 pb-4 space-y-3">
        <h2 className="text-xl font-semibold">버스배차규칙</h2>

        <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-4">
          <section className="bg-white">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-semibold">규칙 목록</h3>
              <Button onClick={fetchData} disabled={loading} className="px-3 py-1 text-sm">
                {loading ? "조회 중..." : "새로고침"}
              </Button>
            </div>
            <div className="max-h-[72vh] overflow-auto border border-gray-300">
              <table className="w-full min-w-[980px] border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-blue-500 text-white">
                  <tr>
                    <th className="border p-2 w-12">순번</th>
                    <th className="border p-2">규칙명</th>
                    <th className="border p-2 w-36">규칙 그룹</th>
                    <th className="border p-2">적용 노선명</th>
                    <th className="border p-2 w-28">배정 방식</th>
                    <th className="border p-2 w-36">요일</th>
                    <th className="border p-2 w-16">우선</th>
                    <th className="border p-2 w-16">사용</th>
                    <th className="border p-2 w-28">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="border p-6 text-center text-gray-500">
                        등록된 배차 규칙이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    rules.map((rule, index) => (
                      <tr key={rule.id} className="hover:bg-gray-50">
                        <td className="border p-2 text-center">{index + 1}</td>
                        <td className="border p-2">{rule.name}</td>
                        <td className="border p-2 text-center">{rule.options_json?.rule_group || "-"}</td>
                        <td className="border p-2">
                          <button
                            type="button"
                            className="text-left text-blue-600 underline hover:text-blue-800"
                            onClick={() => editRule(rule)}
                          >
                            {formatRuleRoutes(rule)}
                          </button>
                        </td>
                        <td className="border p-2 text-center">{formatRuleType(rule.rule_type)}</td>
                        <td className="border p-2 text-center">{formatWeekdays(rule.weekdays || [])}</td>
                        <td className="border p-2 text-center">{rule.priority}</td>
                        <td className="border p-2 text-center">{rule.enabled ? "사용" : "중지"}</td>
                        <td className="border p-2 text-center">
                          <div className="flex justify-center gap-1">
                            <button className="rounded border px-2 py-1 text-xs hover:bg-gray-100" onClick={() => editRule(rule)}>
                              수정
                            </button>
                            <button className="rounded border px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => handleDelete(rule)}>
                              삭제
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-semibold">{form.id ? "규칙 수정" : "규칙 등록"}</h3>
              <Button onClick={resetForm} className="px-3 py-1 text-sm">
                새 규칙
              </Button>
            </div>

            <div className="space-y-3 border border-gray-300 p-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold">규칙명</span>
                  <Input value={form.name} onChange={(e) => updateForm("name", e.target.value)} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold">규칙 그룹</span>
                  <Input
                    list="dispatch-rule-groups"
                    value={form.ruleGroup}
                    onChange={(e) => updateForm("ruleGroup", e.target.value)}
                    placeholder="비워두면 독립 규칙"
                  />
                  <datalist id="dispatch-rule-groups">
                    {ruleGroupOptions.map((group) => (
                      <option key={group} value={group} />
                    ))}
                  </datalist>
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold">노선 조회일</span>
                  <Input type="date" value={form.route_lookup_date} onChange={(e) => updateForm("route_lookup_date", e.target.value)} />
                </label>
                <div className="md:col-span-2">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="block text-sm font-semibold">적용 노선명</span>
                    <Button type="button" onClick={loadRouteOptions} disabled={routeLoading} className="px-3 py-1 text-sm">
                      {routeLoading ? "노선 조회 중..." : "노선 조회"}
                    </Button>
                  </div>
                  {form.rule_type === "rotation" || form.rule_type === "balanced" ? (
                    <div className="rounded border">
                      <div className="border-b bg-gray-50 px-2 py-1 text-xs text-gray-600">
                        선택한 노선 {form.selectedRoutes.length}개 / 후보 기사 {form.selectedDrivers.length}명
                        {form.rule_type === "balanced" ? " · 같은 날짜의 선택 노선은 같은 기사로 배정" : ""}
                      </div>
                      <div className="max-h-44 overflow-auto">
                        {availableRouteOptions.length === 0 ? (
                          <div className="px-2 py-3 text-center text-sm text-gray-500">
                            노선 조회일을 선택한 뒤 노선 조회를 눌러주세요.
                          </div>
                        ) : (
                          availableRouteOptions.map((option) => (
                            <label key={option.key} className="flex items-center justify-between gap-2 border-b px-2 py-1 text-sm last:border-b-0">
                              <span>{formatRouteLabel(option.route, option.time)} {option.count ? <span className="text-gray-500">({option.count}건)</span> : null}</span>
                              <input
                                type="checkbox"
                                checked={form.selectedRoutes.includes(option.key)}
                                onChange={() => toggleRoute(option.key)}
                              />
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  ) : (
                    <select
                      className="h-9 w-full rounded border px-2"
                      value={makeRouteKey(form.route_pattern, form.route_time)}
                      onChange={(e) => {
                        const parsed = parseRouteKey(e.target.value);
                        setForm((prev) => ({
                          ...prev,
                          route_pattern: parsed.route,
                          route_time: parsed.time,
                          selectedRoutes: e.target.value ? [e.target.value] : [],
                        }));
                      }}
                    >
                      <option value="">노선을 선택하세요</option>
                      {routeOptions.map((option) => (
                        <option key={option.key} value={option.key}>
                          {formatRouteLabel(option.route, option.time)} ({option.count}건)
                        </option>
                      ))}
                      {form.route_pattern && !routeOptions.some((option) => option.key === makeRouteKey(form.route_pattern, form.route_time)) && (
                        <option value={makeRouteKey(form.route_pattern, form.route_time)}>
                          {formatRouteLabel(form.route_pattern, form.route_time)}
                        </option>
                      )}
                    </select>
                  )}
                </div>
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold">배정 방식</span>
                  <select className="h-9 w-full rounded border px-2" value={form.rule_type} onChange={(e) => updateForm("rule_type", e.target.value)}>
                    {RULE_TYPES.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold">우선순위</span>
                  <Input type="number" value={form.priority} onChange={(e) => updateForm("priority", e.target.value)} />
                </label>
                <label className="flex items-center gap-2 pt-6">
                  <input type="checkbox" checked={!!form.enabled} onChange={(e) => updateForm("enabled", e.target.checked)} />
                  <span className="text-sm font-semibold">사용</span>
                </label>
              </div>

              <div>
                <div className="mb-1 text-sm font-semibold">적용 요일</div>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS.map((day) => (
                    <label key={day.key} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-sm">
                      <input type="checkbox" checked={form.weekdays.includes(day.key)} onChange={() => toggleWeekday(day.key)} />
                      {day.label}
                    </label>
                  ))}
                </div>
              </div>

              {form.rule_type === "weekday_fixed" ? (
                <div>
                  <div className="mb-1 text-sm font-semibold">요일별 담당 기사</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {WEEKDAYS.filter((day) => form.weekdays.includes(day.key)).map((day) => (
                      <label key={day.key} className="flex items-center gap-2">
                        <span className="w-10 text-sm font-semibold">{day.label}</span>
                        <select
                          className="h-9 flex-1 rounded border px-2"
                          value={form.weekdayDrivers[day.key] || ""}
                          onChange={(e) => setWeekdayDriver(day.key, e.target.value)}
                        >
                          <option value="">기사 선택</option>
                          {drivers.map((driver) => (
                            <option key={driver.id} value={driver.id}>
                              {driver.driver_name || "이름 없음"} {driver.car_number ? `(${driver.car_number})` : ""}
                            </option>
                          ))}
                        </select>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-1 text-sm font-semibold">후보 기사</div>
                  <div className="max-h-44 overflow-auto rounded border">
                    {drivers.map((driver) => (
                      <label key={driver.id} className="flex items-center justify-between gap-2 border-b px-2 py-1 text-sm last:border-b-0">
                        <span>
                          {driver.driver_name || "이름 없음"}
                          <span className="ml-1 text-gray-500">{driver.car_number || ""}</span>
                        </span>
                        <input type="checkbox" checked={form.selectedDrivers.includes(driver.id)} onChange={() => toggleDriver(driver.id)} />
                      </label>
                    ))}
                  </div>
                  {form.rule_type === "rotation" && (
                    <label className="mt-2 block">
                      <span className="mb-1 block text-sm font-semibold">최초 적용 기본 기사</span>
                      <select
                        className="h-9 w-full rounded border px-2"
                        value={form.rotationStartDriverId}
                        onChange={(e) => updateForm("rotationStartDriverId", e.target.value)}
                      >
                        <option value="">첫 번째 후보 기사부터</option>
                        {selectedDriverRows.map((driver) => (
                          <option key={driver.id} value={driver.id}>
                            {driver.driver_name || "이름 없음"} {driver.car_number ? `(${driver.car_number})` : ""}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        적용 이력이 없을 때만 기본 시작 기사로 사용합니다. 실제 적용 시작 기사는 배차처리의 배차규칙 적용 화면에서 조정합니다.
                      </p>
                    </label>
                  )}
                  {(form.rule_type === "rotation" || form.rule_type === "balanced") && form.selectedRoutes.length > 0 && selectedDriverRows.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-1 text-sm font-semibold">기사별 가능 노선</div>
                      <div className="max-h-56 overflow-auto rounded border">
                        <table className="w-full min-w-[560px] border-collapse text-xs">
                          <thead className="sticky top-0 bg-gray-50">
                            <tr>
                              <th className="border px-2 py-1 text-left">기사</th>
                              {form.selectedRoutes.map((routeKey) => {
                                const route = parseRouteKey(routeKey);
                                return (
                                  <th key={routeKey} className="border px-2 py-1 text-center font-medium">
                                    {formatRouteLabel(route.route, route.time)}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDriverRows.map((driver) => (
                              <tr key={driver.id} className="hover:bg-gray-50">
                                <td className="border px-2 py-1">
                                  {driver.driver_name || "이름 없음"}
                                  <span className="ml-1 text-gray-500">{driver.car_number || ""}</span>
                                </td>
                                {form.selectedRoutes.map((routeKey) => (
                                  <td key={`${driver.id}-${routeKey}`} className="border px-2 py-1 text-center">
                                    <input
                                      type="checkbox"
                                      checked={getAllowedRouteKeys(form, driver.id).includes(routeKey)}
                                      onChange={() => toggleDriverRoutePermission(driver.id, routeKey)}
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        체크된 노선만 해당 기사의 배정 후보로 사용됩니다. 새로 선택한 기사는 기본적으로 모든 선택 노선이 가능합니다.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <label className="block">
                <span className="mb-1 block text-sm font-semibold">메모</span>
                <textarea
                  className="h-20 w-full rounded border px-2 py-1 text-sm"
                  value={form.memo}
                  onChange={(e) => updateForm("memo", e.target.value)}
                />
              </label>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? "저장 중..." : "저장"}
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
